import { Component } from '@verndale/core';
import { isLargeDesktop } from '../../helpers';

class LocationMenu extends Component {
  outsideClickListener: EventListenerOrEventListenerObject;
  resizeObserver: ResizeObserver;
  widthAdded: boolean;
  escapeKeyListener: EventListener;

  constructor(el: HTMLElement) {
    super(el);
    this.outsideClickListener = this.handleOutsideClick.bind(this);
    this.resizeObserver = new ResizeObserver(this.adjustDropdownWidth.bind(this));
    this.widthAdded = false;
    this.escapeKeyListener = this.handleEscapeKeyPress.bind(this) as EventListener;
  }

  setupDefaults() {
    this.dom = {
      header: this.el.closest<HTMLElement>('header'),
      trigger: this.el.querySelector<HTMLElement>('.location-menu__button'),
      dropdown: this.el.querySelector<HTMLElement>('.location-menu__dropdown'),
      backButton: this.el.querySelector<HTMLElement>('.location-menu__back-button'),
      accordions: this.el.querySelectorAll<HTMLElement>('.location-menu__accordion'),
      sublists: this.el.querySelectorAll<HTMLElement>('.location-menu__sublist'),
    };

    this.dom.navigationPrimaryItems = (this.dom.header as HTMLElement).querySelectorAll<HTMLElement>('.global-navigation__items__navigation-item');
    this.dom.navigationSecondaryItems = (this.dom.header as HTMLElement).querySelectorAll<HTMLElement>('.global-navigation__menu-column');
    this.dom.navigationPrimaryItemsContainer = (this.dom.header as HTMLElement).querySelector<HTMLElement>('.global-navigation__items');

    Array.from((this.dom.dropdown as HTMLElement).querySelectorAll('a, button, input, select, textarea')).forEach((focusableElement) => {
      focusableElement.setAttribute('tabindex', '-1');
    });

    this.resizeObserver = new ResizeObserver(this.adjustDropdownWidth.bind(this));
    this.resizeObserver.observe(this.dom.dropdown as HTMLElement);
  }

  addListeners() {
    (this.dom.trigger as HTMLElement).addEventListener('click', this.handleToggleDropdown.bind(this));
    (this.dom.backButton as HTMLElement).addEventListener('click', this.handleBackToMenu.bind(this));

    Array.from(this.dom.accordions as NodeListOf<HTMLElement>).forEach((accordion) => {
      accordion.addEventListener('click', this.handleAccordionClick.bind(this));
    });
  }

  openAccordion(accordion: HTMLElement) {
    const trigger = accordion.querySelector('.location-menu__accordion') as HTMLElement;
    const panel = accordion.querySelector('.location-menu__pannel') as HTMLElement;

    Array.from(panel.querySelectorAll('a, button, input, select, textarea')).forEach((focusableElement) => {
      focusableElement.setAttribute('tabindex', '0');
    });

    accordion.classList.add('location-menu__item--open');
    trigger.setAttribute('aria-expanded', 'true');

    if (panel) {
      panel.style.display = 'block';
      const pannelContent = panel.querySelector<HTMLElement>('.location-menu__sublist');

      panel.style.height = `${pannelContent?.offsetHeight}px`;

      setTimeout(() => (panel.style.height = 'auto'), 250);
    }
  }

  closeAccordion(accordion: HTMLElement) {
    const trigger = accordion.querySelector('.location-menu__accordion') as HTMLElement;
    const panel = accordion.querySelector('.location-menu__pannel') as HTMLElement;

    Array.from(panel.querySelectorAll('a, button, input, select, textarea')).forEach((focusableElement) => {
      focusableElement.setAttribute('tabindex', '-1');
    });

    accordion.classList.remove('location-menu__item--open');
    trigger.setAttribute('aria-expanded', 'false');

    if (panel) {
      const pannelContent = panel.querySelector<HTMLElement>('.location-menu__sublist');
      panel.style.height = `${pannelContent?.offsetHeight}px`;
      setTimeout(() => (panel.style.height = '0px'));
      setTimeout(() => (panel.style.display = 'none'), 500);
    }
  }

  handleAccordionClick(event: Event) {
    const target = event.target as HTMLElement;
    const parent = target.closest('.location-menu__item') as HTMLElement;

    if (parent.classList.contains('location-menu__item--open')) {
      this.closeAccordion(parent);
      return;
    }

    this.openAccordion(parent);
  }

  openDropdown() {
    (this.dom.trigger as HTMLElement).setAttribute('aria-expanded', 'true');
    this.el.classList.add('location-menu--open');
    this.el.parentElement?.classList.add('utility-navigation__location-menu--open');
    const utilityNav = this.el.closest('.utility-navigation') as HTMLElement;


    if (!isLargeDesktop()) {
      (this.dom.navigationPrimaryItemsContainer as HTMLElement).classList.add('global-navigation__items--hidden');
      (this.dom.dropdown as HTMLElement).style.blockSize = `calc(100% - 68px - ${utilityNav.offsetHeight}px)`;
    }

    document.addEventListener('click', this.outsideClickListener);
    document.addEventListener('keydown', this.escapeKeyListener);
  }

  closeDropdown() {
    (this.dom.trigger as HTMLElement).setAttribute('aria-expanded', 'false');
    this.el.classList.remove('location-menu--open');
    this.el.parentElement?.classList.remove('utility-navigation__location-menu--open');

    Array.from(this.dom.navigationPrimaryItems as NodeListOf<HTMLElement>).forEach((navigationItem) => {
      navigationItem.classList.remove('global-navigation__items__navigation-item--active');
      (this.dom.navigationPrimaryItemsContainer as HTMLElement).classList.remove('global-navigation__items--hidden');
    });

    Array.from(this.dom.navigationSecondaryItems as NodeListOf<HTMLElement>).forEach((navigationItem) => {
      const secondary = navigationItem.closest('.global-navigation__items__navigation-item') as HTMLElement;
      secondary.classList.remove('global-navigation__items__navigation-item--hidden');  
      navigationItem.classList.remove('--active');
    });
    
    document.removeEventListener('click', this.outsideClickListener);
    document.removeEventListener('keydown', this.escapeKeyListener);
  }

  handleToggleDropdown() {
    if (this.el.classList.contains('location-menu--open')) {
      this.closeDropdown();
      return;
    }

    this.openDropdown();
  }

  handleBackToMenu() {
    this.closeDropdown();
  }

  handleOutsideClick(event: Event) {
    const isClickInside = this.el.contains(event.target as Node);
    if (!isClickInside) {
      this.closeDropdown();
    }
  }

  handleEscapeKeyPress(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.closeDropdown();
    }
  }

  getScrollbarWidth() {
    const container = document.createElement('div');
    container.style.overflow = 'scroll';
    container.style.visibility = 'hidden';
    container.style.position = 'absolute';
    container.style.top = '0';
    container.style.left = '0';
    container.style.width = '100px';
    container.style.height = '100px';
    document.body.appendChild(container);
  
    const inner = document.createElement('div');
    inner.style.width = '100%';
    inner.style.height = '100%';
    container.appendChild(inner);
  
    const scrollbarWidth = container.offsetWidth - inner.offsetWidth;
    document.body.removeChild(container);
  
    return scrollbarWidth;
  }

  adjustDropdownWidth() {
    if (!isLargeDesktop()) return;

    const dropdown = this.dom.dropdown as HTMLElement;
    const hasScrollbar = dropdown.scrollHeight > dropdown.clientHeight;
    const scrollbarWidth = this.getScrollbarWidth();
    
    if (hasScrollbar && !this.widthAdded) {
      dropdown.style.insetInlineEnd = `-${scrollbarWidth}px`;
      dropdown.style.inlineSize = `${dropdown.offsetWidth + scrollbarWidth}px`;
      this.widthAdded = true;
    } else if (!hasScrollbar && this.widthAdded) {
      dropdown.style.insetInlineEnd = '0';
      dropdown.style.inlineSize = 'auto';
      this.widthAdded = false;
    }
  }
}

export default LocationMenu;